import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  UtensilsCrossed, CreditCard, Mail, Download, Send, Save,
  ChevronDown, ChevronUp, FileText, Clock, AlertCircle,
  Loader2, Plus, Edit, Trash2, Upload, Camera, MapPin, X, Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Photo Upload Button ───
const PhotoUpload = ({ currentPhoto, onUploaded, token, label }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      onUploaded(res.data.url);
      toast.success('Photo uploadee');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur upload');
    }
    finally { setUploading(false); }
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <button onClick={() => fileRef.current?.click()}
        className="relative w-20 h-20 rounded-full overflow-hidden border-2 border-dashed border-[#D4AF37]/40 hover:border-[#D4AF37] transition-all group cursor-pointer bg-[#F8F9FA]"
        data-testid={`upload-${label}`}>
        {currentPhoto ? (
          <img src={`${BASE_URL}${currentPhoto}`} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-[#94A3B8] group-hover:text-[#D4AF37] transition-colors">
            <Camera className="h-5 w-5" />
            <span className="text-[8px] mt-0.5">Photo</span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="h-5 w-5 text-white animate-spin" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
          <Upload className="h-4 w-4 text-white drop-shadow" />
        </div>
      </button>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} className="hidden" />
    </div>
  );
};

// ─── Crew Section ───
const CrewSection = ({ token }) => {
  const [crew, setCrew] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  const fetchCrew = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/crew`, { headers: { Authorization: `Bearer ${token}` } });
      setCrew(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchCrew(); }, [fetchCrew]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/crew`, crew, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Equipage mis a jour');
      setEditing(false);
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const updateMember = (role, field, value) => {
    setCrew(prev => ({ ...prev, [role]: { ...prev[role], [field]: value } }));
  };

  if (loading || !crew) return null;

  const hasData = crew.captain?.name || crew.cook?.name || crew.captain?.photo || crew.cook?.photo;

  return (
    <div className="bg-gradient-to-r from-[#0A2540]/[0.03] to-[#2A9D8F]/[0.03] rounded-xl p-4 border border-[#E2E8F0]" data-testid="crew-section">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-sm text-[#0A2540] flex items-center gap-2">
          Votre equipage
          {!hasData && <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] text-[9px]">A configurer</Badge>}
        </h4>
        <Button variant="ghost" size="sm" className="h-7 text-xs text-[#4A5568]"
          onClick={() => setEditing(!editing)} data-testid="edit-crew-btn">
          <Edit className="h-3 w-3 mr-1" />{editing ? 'Fermer' : 'Modifier'}
        </Button>
      </div>

      <div className="flex items-center justify-center gap-8">
        {['captain', 'cook'].map(role => (
          <div key={role} className="flex flex-col items-center gap-1.5">
            {editing ? (
              <>
                <PhotoUpload currentPhoto={crew[role]?.photo} token={token}
                  label={role} onUploaded={(url) => updateMember(role, 'photo', url)} />
                <Input value={crew[role]?.name || ''} onChange={e => updateMember(role, 'name', e.target.value)}
                  className="h-7 text-xs text-center w-32" placeholder="Prenom / Nom" />
                <Input value={crew[role]?.title || ''} onChange={e => updateMember(role, 'title', e.target.value)}
                  className="h-6 text-[10px] text-center w-28 text-[#94A3B8]" placeholder="Titre" />
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#D4AF37]/30 bg-[#F8F9FA]">
                  {crew[role]?.photo ? (
                    <img src={`${BASE_URL}${crew[role].photo}`} alt={crew[role]?.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex items-center justify-center h-full text-[#94A3B8]">
                      <Camera className="h-5 w-5" />
                    </div>
                  )}
                </div>
                <span className="font-semibold text-xs text-[#0A2540]">{crew[role]?.name || 'Non renseigne'}</span>
                <span className="text-[10px] text-[#C4724F] font-medium">{crew[role]?.title}</span>
              </>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <div className="flex justify-center mt-3">
          <Button size="sm" className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white h-7 text-xs"
            onClick={handleSave} disabled={saving} data-testid="save-crew-btn">
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
            Enregistrer
          </Button>
        </div>
      )}
    </div>
  );
};

// ─── Suppliers Manager ───
const SuppliersSection = ({ token }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', product: '', location: '', description: '', photo: '' });
  const [saving, setSaving] = useState(false);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/suppliers`, { headers: { Authorization: `Bearer ${token}` } });
      setSuppliers(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/suppliers/${editId}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Prestataire mis a jour');
      } else {
        await axios.post(`${API}/suppliers`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Prestataire ajoute');
      }
      fetchSuppliers();
      resetForm();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce prestataire ?')) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Prestataire supprime');
      fetchSuppliers();
    } catch { toast.error('Erreur'); }
  };

  const startEdit = (s) => {
    setForm({ name: s.name, product: s.product, location: s.location, description: s.description || '', photo: s.photo || '' });
    setEditId(s.id);
    setShowAdd(true);
  };

  const resetForm = () => {
    setForm({ name: '', product: '', location: '', description: '', photo: '' });
    setEditId(null);
    setShowAdd(false);
  };

  if (loading) return null;

  return (
    <div className="space-y-3" data-testid="suppliers-section">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm text-[#0A2540] flex items-center gap-2">
          <MapPin className="h-4 w-4 text-[#2A9D8F]" />
          Nos partenaires locaux — Circuit court
          <Badge className="bg-[#2A9D8F]/10 text-[#2A9D8F] text-[9px]">{suppliers.length}</Badge>
        </h4>
        <Button size="sm" variant="outline" className="h-7 text-xs border-[#2A9D8F] text-[#2A9D8F]"
          onClick={() => { resetForm(); setShowAdd(!showAdd); }} data-testid="add-supplier-btn">
          <Plus className="h-3 w-3 mr-1" />Ajouter
        </Button>
      </div>

      {/* Add/Edit form */}
      {showAdd && (
        <div className="bg-[#2A9D8F]/5 border border-dashed border-[#2A9D8F]/30 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h5 className="font-semibold text-xs text-[#0A2540]">{editId ? 'Modifier' : 'Ajouter'} un prestataire</h5>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={resetForm}><X className="h-3.5 w-3.5" /></Button>
          </div>
          <div className="flex items-start gap-3">
            <PhotoUpload currentPhoto={form.photo} token={token} label="supplier"
              onUploaded={(url) => setForm(f => ({ ...f, photo: url }))} />
            <div className="flex-1 space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-[10px]">Nom *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-sm" placeholder="Bergerie Castellani" /></div>
                <div><Label className="text-[10px]">Produit</Label><Input value={form.product} onChange={e => setForm(f => ({ ...f, product: e.target.value }))} className="h-8 text-sm" placeholder="Fromages de brebis" /></div>
                <div><Label className="text-[10px]">Localisation</Label><Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="h-8 text-sm" placeholder="Zonza, Corse" /></div>
              </div>
              <div><Label className="text-[10px]">Description courte</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="h-8 text-sm" placeholder="Fromages artisanaux au lait cru..." /></div>
              <Button size="sm" className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white h-7 text-xs"
                onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                {editId ? 'Modifier' : 'Ajouter'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Suppliers list */}
      {suppliers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {suppliers.map(s => (
            <div key={s.id} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2.5 border border-[#E2E8F0] hover:border-[#2A9D8F]/30 transition-all group"
              data-testid={`supplier-${s.id}`}>
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#F8F9FA] shrink-0 border border-[#E2E8F0]">
                {s.photo ? (
                  <img src={`${BASE_URL}${s.photo}`} alt={s.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full text-[#94A3B8]"><MapPin className="h-4 w-4" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-xs text-[#0A2540] truncate">{s.name}</div>
                <div className="text-[10px] text-[#4A5568] truncate">{s.product}{s.location ? ` — ${s.location}` : ''}</div>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => startEdit(s)}><Edit className="h-3 w-3" /></Button>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => handleDelete(s.id)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      ) : !showAdd && (
        <div className="text-center py-4 text-xs text-[#94A3B8] bg-[#F8F9FA] rounded-lg">
          <MapPin className="h-6 w-6 mx-auto mb-1 opacity-40" />
          Ajoutez vos producteurs et artisans locaux
        </div>
      )}
    </div>
  );
};

// ─── Menu Editor ───
const MenuTab = ({ departure, token }) => {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);

  const fetchMenu = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/departures/${departure.id}/menu`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMenu(res.data);
    } catch { toast.error('Erreur chargement menu'); }
    finally { setLoading(false); }
  }, [departure.id, token]);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  const updateDay = (idx, field, value) => {
    setMenu(prev => {
      const days = [...prev.days];
      days[idx] = { ...days[idx], [field]: value };
      return { ...prev, days, is_default: false };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/departures/${departure.id}/menu`, { days: menu.days }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Menu enregistre');
      setMenu(prev => ({ ...prev, is_default: false }));
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const addDay = () => {
    setMenu(prev => {
      const days = [...(prev.days || [])];
      const newDayNum = days.length + 1;
      days.push({
        day: newDayNum,
        label: `Jour ${newDayNum}`,
        breakfast: '',
        lunch: '',
        dinner: '',
        notes: '',
        breakfast_supplier: '',
        lunch_supplier: '',
        dinner_supplier: '',
      });
      return { ...prev, days, is_default: false };
    });
  };

  const removeDay = (idx) => {
    if (!window.confirm(`Supprimer Jour ${menu.days[idx]?.day} ?`)) return;
    setMenu(prev => {
      const days = prev.days.filter((_, i) => i !== idx);
      // Re-number days
      const renumbered = days.map((d, i) => ({ ...d, day: i + 1, label: d.label.startsWith('Jour ') ? `Jour ${i + 1}${d.label.includes(' - ') ? d.label.substring(d.label.indexOf(' - ')) : ''}` : d.label }));
      return { ...prev, days: renumbered, is_default: false };
    });
  };

  if (loading) return <div className="py-8 text-center text-sm text-[#94A3B8]"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Chargement du menu...</div>;
  if (!menu) return null;

  return (
    <div className="space-y-4" data-testid="menu-editor">
      {/* Crew section */}
      <CrewSection token={token} />

      {/* Menu header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold text-sm text-[#0A2540]">Menu de bord</h4>
          {menu.is_default && <Badge className="bg-blue-50 text-blue-600 text-[10px]">Menu par defaut</Badge>}
          <span className="text-xs text-[#94A3B8]">{menu.days?.length || 0} jours</span>
        </div>
        <Button size="sm" className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white h-8 text-xs"
          onClick={handleSave} disabled={saving} data-testid="save-menu-btn">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
          Enregistrer le menu
        </Button>
      </div>

      {/* Days */}
      <div className="space-y-1.5">
        {menu.days?.map((day, idx) => (
          <div key={idx} className="border border-[#E2E8F0] rounded-lg overflow-hidden transition-all hover:border-[#2A9D8F]/40">
            <button className="w-full flex items-center justify-between px-4 py-2.5 bg-white hover:bg-[#F8F9FA] transition-colors"
              onClick={() => setExpandedDay(expandedDay === idx ? null : idx)}>
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 bg-[#0A2540] rounded-md flex items-center justify-center">
                  <span className="text-white text-xs font-bold">J{day.day}</span>
                </div>
                <span className="font-medium text-sm text-[#0A2540]">{day.label}</span>
                {day.notes && <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] text-[9px]">{day.notes}</Badge>}
              </div>
              <div className="flex items-center gap-1">
                <span className="p-1 rounded hover:bg-red-50 text-[#94A3B8] hover:text-red-500 transition-colors"
                  onClick={(e) => { e.stopPropagation(); removeDay(idx); }} role="button"
                  data-testid={`remove-day-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </span>
                {expandedDay === idx ? <ChevronUp className="h-4 w-4 text-[#94A3B8]" /> : <ChevronDown className="h-4 w-4 text-[#94A3B8]" />}
              </div>
            </button>
            {expandedDay === idx && (
              <div className="px-4 pb-4 pt-2 bg-[#F8F9FA]/50 space-y-3 border-t border-[#E2E8F0]">
                {/* Label editable */}
                <div>
                  <Label className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Intitule du jour</Label>
                  <Input value={day.label || ''} onChange={e => updateDay(idx, 'label', e.target.value)}
                    className="text-sm h-8 mt-1 font-medium" placeholder="Ex: Jour 3 - Bonifacio" />
                </div>
                {/* Meals with supplier annotations */}
                {[
                  { key: 'breakfast', label: 'Petit-dejeuner', supplierKey: 'breakfast_supplier' },
                  { key: 'lunch', label: 'Dejeuner', supplierKey: 'lunch_supplier' },
                  { key: 'dinner', label: 'Diner', supplierKey: 'dinner_supplier' },
                ].map(meal => (
                  <div key={meal.key} className="space-y-1">
                    <Label className="text-[10px] text-[#94A3B8] uppercase tracking-wider">{meal.label}</Label>
                    <Textarea value={day[meal.key] || ''} onChange={e => updateDay(idx, meal.key, e.target.value)}
                      className="text-sm min-h-[50px] resize-none" placeholder={`${meal.label}...`} />
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3 w-3 text-[#2A9D8F] shrink-0" />
                      <Input value={day[meal.supplierKey] || ''} onChange={e => updateDay(idx, meal.supplierKey, e.target.value)}
                        className="h-6 text-[11px] border-dashed border-[#2A9D8F]/30 bg-[#2A9D8F]/[0.03] placeholder:text-[#94A3B8]"
                        placeholder="Producteur local (ex: Poisson — Pecheur J-Pierre, Ajaccio)" />
                    </div>
                  </div>
                ))}
                <div>
                  <Label className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Notes / Evenement</Label>
                  <Input value={day.notes || ''} onChange={e => updateDay(idx, 'notes', e.target.value)}
                    className="text-sm h-8 mt-1" placeholder="Ex: Soiree de gala, diner du capitaine..." />
                </div>
              </div>
            )}
          </div>
        ))}
        {/* Add day button */}
        <button onClick={addDay}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-[#2A9D8F]/30 rounded-lg text-[#2A9D8F] text-xs font-semibold hover:bg-[#2A9D8F]/5 hover:border-[#2A9D8F]/60 transition-all"
          data-testid="add-day-btn">
          <Plus className="h-3.5 w-3.5" />
          Ajouter un jour
        </button>
      </div>

      {/* Suppliers section */}
      <SuppliersSection token={token} />
    </div>
  );
};

// ─── Boarding Passes ───
const BoardingPassTab = ({ departure, token }) => {
  const passengers = departure.passengers || [];
  const [downloading, setDownloading] = useState(null);

  const downloadBoardingPass = async (passenger) => {
    setDownloading(passenger.id);
    try {
      const res = await axios.get(
        `${API}/departures/${departure.id}/boarding-pass/${passenger.id}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Carte_Embarquement_${passenger.name.replace(/\s+/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Carte de ${passenger.name} telechargee`);
    } catch { toast.error('Erreur generation PDF'); }
    finally { setDownloading(null); }
  };

  const downloadAll = async () => {
    for (const p of passengers) {
      await downloadBoardingPass(p);
      await new Promise(r => setTimeout(r, 300));
    }
  };

  if (passengers.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-[#94A3B8]" data-testid="boarding-pass-empty">
        <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-40" />
        Ajoutez des passagers pour generer les cartes d'embarquement.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="boarding-pass-tab">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm text-[#0A2540]">
          Cartes d'embarquement ({passengers.length})
        </h4>
        <Button size="sm" variant="outline" className="h-8 text-xs border-[#0A2540] text-[#0A2540]"
          onClick={downloadAll} data-testid="download-all-bp">
          <Download className="h-3.5 w-3.5 mr-1" />Tout telecharger
        </Button>
      </div>
      <div className="space-y-2">
        {passengers.map(p => (
          <div key={p.id} className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-[#E2E8F0] hover:border-[#D4AF37]/40 transition-all group">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0A2540] rounded-lg flex items-center justify-center shrink-0">
                <span className="text-[#D4AF37] text-xs font-bold">C{p.cabin_number || '?'}</span>
              </div>
              <div>
                <span className="font-semibold text-sm text-[#0A2540]">{p.name}</span>
                <div className="text-xs text-[#94A3B8]">Cabine {p.cabin_number} {p.email ? `- ${p.email}` : ''}</div>
              </div>
            </div>
            <Button size="sm" className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white h-8 text-xs opacity-80 group-hover:opacity-100 transition-opacity"
              onClick={() => downloadBoardingPass(p)} disabled={downloading === p.id}
              data-testid={`download-bp-${p.id}`}>
              {downloading === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
              Telecharger PDF
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Email Tab ───
const EMAIL_TEMPLATES = {
  boarding_pass: {
    label: 'Cartes d\'embarquement',
    subject: 'Votre carte d\'embarquement SOGNUDIMARE',
    body: 'Nous avons le plaisir de vous transmettre votre carte d\'embarquement pour votre prochaine croisiere.\n\nVeuillez trouver ci-joint votre carte d\'embarquement nominative au format PDF. Nous vous invitons a la conserver precieusement et a la presenter lors de votre arrivee au port.\n\nN\'hesitez pas a nous contacter pour toute question.',
  },
  menu: {
    label: 'Menu de la croisiere',
    subject: 'Le menu de votre croisiere SOGNUDIMARE',
    body: 'Nous avons le plaisir de vous communiquer le menu qui vous sera propose durant votre croisiere.\n\nSi vous avez des allergies alimentaires ou des regimes specifiques, merci de nous le signaler au plus vite afin que notre cuisiniere puisse adapter les repas.\n\nBon appetit et bon voyage !',
  },
  info: {
    label: 'Informations pratiques',
    subject: 'Informations pratiques pour votre croisiere',
    body: 'Votre depart approche ! Voici quelques rappels importants :\n\n- Rendez-vous au port le jour J a partir de 16h00\n- Pieces d\'identite en cours de validite obligatoires\n- Bagages souples uniquement (pas de valises rigides)\n- Prevoir : maillot, protection solaire, chaussures de pont\n- Pension complete incluse a bord\n\nNous avons hate de vous accueillir !',
  },
  custom: {
    label: 'Message personnalise',
    subject: '',
    body: '',
  },
};

const EmailTab = ({ departure, token, cruiseName }) => {
  const passengers = departure.passengers || [];
  const [emailType, setEmailType] = useState('boarding_pass');
  const [subject, setSubject] = useState(EMAIL_TEMPLATES.boarding_pass.subject);
  const [body, setBody] = useState(EMAIL_TEMPLATES.boarding_pass.body);
  const [selectedPassengers, setSelectedPassengers] = useState('all');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    const tpl = EMAIL_TEMPLATES[emailType];
    if (tpl) { setSubject(tpl.subject); setBody(tpl.body); }
  }, [emailType]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/departures/${departure.id}/email-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data);
    } catch { /* silent */ }
    finally { setLoadingHistory(false); }
  }, [departure.id, token]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const passengersWithEmail = passengers.filter(p => p.email);

  const handleSend = async () => {
    if (!subject.trim()) { toast.error('Objet requis'); return; }
    if (!body.trim()) { toast.error('Message requis'); return; }
    if (passengersWithEmail.length === 0) { toast.error('Aucun passager avec email'); return; }

    const confirmMsg = selectedPassengers === 'all'
      ? `Envoyer a ${passengersWithEmail.length} passager(s) ?`
      : 'Envoyer au passager selectionne ?';
    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    try {
      const payload = { type: emailType, subject, body };
      if (selectedPassengers !== 'all') {
        payload.passenger_ids = [selectedPassengers];
      }
      const res = await axios.post(`${API}/departures/${departure.id}/send-emails`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="email-tab">
      <div className="space-y-3">
        <h4 className="font-semibold text-sm text-[#0A2540]">Envoyer un email</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Type de message</Label>
            <Select value={emailType} onValueChange={setEmailType}>
              <SelectTrigger className="mt-1 h-9 text-sm" data-testid="email-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(EMAIL_TEMPLATES).map(([key, tpl]) => (
                  <SelectItem key={key} value={key}>{tpl.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Destinataires</Label>
            <Select value={selectedPassengers} onValueChange={setSelectedPassengers}>
              <SelectTrigger className="mt-1 h-9 text-sm" data-testid="email-recipients-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les passagers ({passengersWithEmail.length})</SelectItem>
                {passengersWithEmail.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name} ({p.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Objet</Label>
          <Input value={subject} onChange={e => setSubject(e.target.value)} className="text-sm h-9 mt-1" data-testid="email-subject" />
        </div>
        <div>
          <Label className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Message</Label>
          <Textarea value={body} onChange={e => setBody(e.target.value)} className="text-sm min-h-[120px] mt-1" data-testid="email-body" />
        </div>
        {emailType === 'boarding_pass' && (
          <div className="flex items-center gap-2 text-xs text-[#2A9D8F] bg-[#2A9D8F]/5 rounded-lg px-3 py-2">
            <FileText className="h-3.5 w-3.5 shrink-0" />
            Les cartes d'embarquement PDF seront jointes automatiquement.
          </div>
        )}
        {passengersWithEmail.length === 0 && (
          <div className="flex items-center gap-2 text-xs text-[#C4724F] bg-[#C4724F]/5 rounded-lg px-3 py-2">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            Aucun passager n'a d'adresse email.
          </div>
        )}
        <Button className="bg-[#C4724F] hover:bg-[#C4724F]/90 text-white h-9 text-sm"
          onClick={handleSend} disabled={sending || passengersWithEmail.length === 0} data-testid="send-email-btn">
          {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
          Envoyer {selectedPassengers === 'all' ? `a ${passengersWithEmail.length} passager(s)` : ''}
        </Button>
      </div>

      {!loadingHistory && history.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-[#E2E8F0]">
          <h4 className="font-semibold text-xs text-[#94A3B8] uppercase tracking-wider">Historique des envois</h4>
          <div className="space-y-1.5">
            {history.map((h, i) => (
              <div key={h.id || i} className="flex items-center justify-between bg-[#F8F9FA] rounded-lg px-3 py-2 text-xs">
                <div className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 text-[#4A5568]" />
                  <span className="font-medium text-[#0A2540]">{h.subject}</span>
                  <Badge className="bg-[#0A2540]/5 text-[#4A5568] text-[9px]">
                    {EMAIL_TEMPLATES[h.type]?.label || h.type}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-[#94A3B8]">
                  <span>{h.recipients_count} envoye(s)</span>
                  {h.errors > 0 && <span className="text-red-500">{h.errors} erreur(s)</span>}
                  <Clock className="h-3 w-3" />
                  <span>{new Date(h.sent_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Pre-Boarding Section ───
const PreBoardingSection = ({ departure, token, cruiseName }) => {
  const passengersCount = departure.passengers?.length || 0;

  return (
    <div className="mt-5" data-testid="pre-boarding-section">
      <Tabs defaultValue="menu" className="w-full">
        <TabsList className="w-full bg-[#0A2540]/5 h-10 p-1 rounded-xl">
          <TabsTrigger value="menu"
            className="flex-1 text-xs font-semibold data-[state=active]:bg-[#2A9D8F] data-[state=active]:text-white rounded-lg transition-all"
            data-testid="tab-menu">
            <UtensilsCrossed className="h-3.5 w-3.5 mr-1.5" />
            Menu de bord
          </TabsTrigger>
          <TabsTrigger value="boarding"
            className="flex-1 text-xs font-semibold data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg transition-all"
            data-testid="tab-boarding">
            <CreditCard className="h-3.5 w-3.5 mr-1.5" />
            Cartes ({passengersCount})
          </TabsTrigger>
          <TabsTrigger value="emails"
            className="flex-1 text-xs font-semibold data-[state=active]:bg-[#C4724F] data-[state=active]:text-white rounded-lg transition-all"
            data-testid="tab-emails">
            <Mail className="h-3.5 w-3.5 mr-1.5" />
            Emails
          </TabsTrigger>
        </TabsList>
        <TabsContent value="menu" className="mt-4">
          <MenuTab departure={departure} token={token} />
        </TabsContent>
        <TabsContent value="boarding" className="mt-4">
          <BoardingPassTab departure={departure} token={token} />
        </TabsContent>
        <TabsContent value="emails" className="mt-4">
          <EmailTab departure={departure} token={token} cruiseName={cruiseName} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PreBoardingSection;
